import { useCallback, useState } from 'react'

import { useTelemetrySource } from '../../lib/api/source.tsx'
import type { EventAiFinding, EventAiFindings } from '../../lib/api/aiFindings.ts'
import { formatConfidence, verdictPresentation } from '../../lib/ai/semantics.ts'
import { formatClockShort, formatInt } from '../../lib/format.ts'
import { Link } from '../../lib/router.tsx'
import { useResource } from '../../lib/useResource.ts'
import { Button } from '../primitives/Button.tsx'
import { LoadingRow, Notice } from '../primitives/States.tsx'
import { SeverityBadge } from '../detections/Severity.tsx'
import { CitationBadges, FindingStatusBadge, VerdictBadge } from './Badges.tsx'

export function findingHref(id: string): string {
  return `/analise-ia?achado=${encodeURIComponent(id)}`
}

/**
 * "Análise da IA" na ficha do evento. Carrega só quando a ficha abre, com
 * estado próprio: falhar aqui nunca derruba a ficha.
 */
export function EventAiPanel({ logId, onNavigate }: { logId: number; onNavigate: () => void }) {
  const source = useTelemetrySource()
  const fetcher = useCallback((signal: AbortSignal) => source.logAiFindings(logId, signal), [source, logId])
  const { data, error, loading, reload } = useResource<EventAiFindings>(fetcher)

  return (
    <section className="border-b border-line-subtle px-4 py-3">
      <h3 className="mb-1.5 text-micro font-medium uppercase tracking-label text-fg-secondary">Análise da IA</h3>
      {error !== null ? (
        <div className="flex items-center gap-2">
          <Notice tone="neutral">Não deu para ler a análise deste evento.</Notice>
          <Button size="sm" onClick={reload}>
            Tentar de novo
          </Button>
        </div>
      ) : data === null ? (
        <LoadingRow label="Carregando a análise…" />
      ) : (
        <EventAiFindingsView findings={data.findings} refreshing={loading} onRefresh={reload} onNavigate={onNavigate} />
      )}
    </section>
  )
}

/** O conteúdo do painel, já com a resposta em mãos. Exportado para o render check. */
export function EventAiFindingsView({
  findings,
  refreshing,
  onRefresh,
  onNavigate,
}: {
  findings: readonly EventAiFinding[]
  refreshing: boolean
  onRefresh: () => void
  onNavigate: () => void
}) {
  if (findings.length === 0) {
    return (
      <div className="flex flex-col items-start gap-1.5">
        <p className="text-sm text-fg-secondary">Ainda não analisado.</p>
        <Button size="sm" busy={refreshing} onClick={onRefresh}>
          Atualizar
        </Button>
      </div>
    )
  }
  return (
    <ul className="flex flex-col gap-2">
      {findings.map((item) => (
        <FindingCitation key={item.finding.id} item={item} onNavigate={onNavigate} />
      ))}
    </ul>
  )
}

function FindingCitation({ item, onNavigate }: { item: EventAiFinding; onNavigate: () => void }) {
  const { finding, cited_as } = item
  const verdict = verdictPresentation(finding.verdict)
  const confidence = formatConfidence(finding.confidence)
  const [showFailure, setShowFailure] = useState(false)

  return (
    <li className={`flex flex-col gap-1.5 border-l-2 pl-2.5 ${verdict?.border ?? 'border-line'}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <VerdictBadge verdict={finding.verdict} />
        {finding.severity !== null && <SeverityBadge severity={finding.severity} />}
        {confidence !== null && <span className="tnum font-mono text-sm text-fg">{confidence}</span>}
        <FindingStatusBadge status={finding.status} />
        <span className="ml-auto tnum font-mono text-micro text-fg-muted">
          {formatClockShort(finding.window_start)}–{formatClockShort(finding.window_end)} UTC
        </span>
      </div>

      {finding.hypothesis !== null && <p className="text-sm whitespace-pre-wrap text-fg">{finding.hypothesis}</p>}

      {finding.status === 'triage_only' && (
        <p className="text-sm text-fg-secondary">
          {`Triagem: pontuação ${formatInt(finding.triage_score)}, não foi para a IA`}
        </p>
      )}
      {finding.status === 'awaiting_analysis' && <p className="text-sm text-fg-secondary">Aguardando IA</p>}
      {finding.status === 'analysis_failed' && (
        <div className="flex flex-col items-start gap-1">
          <button
            type="button"
            className="text-sm text-crit underline-offset-2 hover:underline"
            aria-expanded={showFailure}
            onClick={() => setShowFailure((value) => !value)}
          >
            Falha na análise {showFailure ? '▴' : '▾'}
          </button>
          {showFailure && finding.failure_reason !== null && (
            <p className="font-mono text-micro whitespace-pre-wrap break-words text-fg-secondary">
              {finding.failure_reason}
            </p>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <CitationBadges citedAs={cited_as} />
        <Link
          href={findingHref(finding.id)}
          onClick={onNavigate}
          className="ml-auto text-sm text-accent underline-offset-2 hover:underline"
        >
          Abrir achado →
        </Link>
      </div>
    </li>
  )
}
